// ESG demo data: the two demo organizations (outlet + recycler), shaped as the
// app's OrganizationSchema. GET /organization returns both, so the sidebar org
// Autocomplete doubles as the persona switcher.

import {
  OrganizationType,
  type OrganizationRequest,
  type OrganizationSchema,
  type UpdateOrganizationRequest,
} from '../../types/organization.type';
import {
  DEMO_OUTLET_ORG,
  DEMO_RECYCLER_ORG,
  DEMO_SITES,
  DEMO_FACILITIES,
  demoId,
  monthStart,
  type DemoPersona,
} from '../core';
import { isDemoOrgId, nowNaive, onReset } from './common';

function str(v: unknown): string {
  return typeof v === 'string' ? v : '';
}

/** Org "joined" ~10 days before the 12-month reporting window starts. */
function joinedAt(daysBeforeWindow: number): string {
  const d = new Date(monthStart(-11).getTime() - daysBeforeWindow * 86_400_000);
  return d.toISOString().slice(0, 19);
}

function buildOutlet(): OrganizationSchema {
  const o = DEMO_OUTLET_ORG;
  return {
    id: o.id,
    name: o.name,
    organization_type: OrganizationType.WASTE_OUTLET,
    google_maps_url: `https://maps.google.com/?q=${DEMO_SITES[0].lat},${DEMO_SITES[0].lng}`,
    logo_url: null,
    data: {
      name_ar: str(o.name_ar),
      email: str(o.email),
      phone_number: str(o.phone),
      industry: ['Other'],
      more_info:
        'Supermarket chain with 5 branches and a distribution centre in Riyadh and Jeddah. ' +
        'سلسلة متاجر لديها 5 فروع ومركز توزيع في الرياض وجدة.',
      city: str(o.city),
      country: str(o.country),
      cr_number: str(o.cr_number),
      vat_number: str(o.vat_number),
      branches_count: DEMO_SITES.length,
      reporting_currency: 'SAR',
    },
    created_at: joinedAt(12),
    updated_at: joinedAt(2),
  };
}

function buildRecycler(): OrganizationSchema {
  const o = DEMO_RECYCLER_ORG;
  return {
    id: o.id,
    name: o.name,
    organization_type: OrganizationType.RECYCLER,
    google_maps_url: `https://maps.google.com/?q=${DEMO_FACILITIES[0].lat},${DEMO_FACILITIES[0].lng}`,
    logo_url: null,
    data: {
      name_ar: str(o.name_ar),
      email: str(o.email),
      phone_number: str(o.phone),
      industry: ['Paper', 'Plastic', 'Metal', 'Glass'],
      more_info:
        'MWAN-licensed recycler operating the Sulay Sorting Plant (Riyadh) and the Jeddah Transfer Station. ' +
        'شركة إعادة تدوير مرخصة من المركز الوطني لإدارة النفايات.',
      city: str(o.city),
      country: str(o.country),
      cr_number: str(o.cr_number),
      vat_number: str(o.vat_number),
      facilities_count: DEMO_FACILITIES.length,
      reporting_currency: 'SAR',
    },
    created_at: joinedAt(9),
    updated_at: joinedAt(1),
  };
}

let organizations: OrganizationSchema[] = [];
let createdCount = 0;

function ensure(): OrganizationSchema[] {
  if (organizations.length === 0) organizations = [buildOutlet(), buildRecycler()];
  return organizations;
}

onReset(() => {
  organizations = [];
  createdCount = 0;
});

/** The org a persona starts as (what DemoEntry seeds as selectedOrganization). */
export function demoOrganizationForPersona(persona: DemoPersona): OrganizationSchema {
  return persona === 'outlet' ? buildOutlet() : buildRecycler();
}

export function listOrganizations(filter: { name?: string; organization_type?: string } = {}): OrganizationSchema[] {
  const name = filter.name?.trim().toLowerCase();
  return ensure().filter((org) => {
    if (filter.organization_type && org.organization_type !== filter.organization_type) return false;
    if (name) {
      const hay = `${org.name} ${String(org.data?.name_ar ?? '')}`.toLowerCase();
      if (!hay.includes(name)) return false;
    }
    return true;
  });
}

export function getOrganizationById(id: string): OrganizationSchema | undefined {
  return ensure().find((o) => o.id === id);
}

export function organizationName(id: string): string {
  return getOrganizationById(id)?.name ?? 'Organization';
}

export function createOrganizationRecord(body: Partial<OrganizationRequest>): OrganizationSchema {
  createdCount += 1;
  const now = nowNaive();
  const org: OrganizationSchema = {
    // Raw kind code '00' + offset: never collides with the fixed demo org ids.
    id: demoId('00', 0xa100 + createdCount),
    name: body.name?.trim() || `New organization ${createdCount}`,
    organization_type: (body.organization_type as OrganizationType) ?? OrganizationType.WASTE_OUTLET,
    google_maps_url: body.google_maps_url ?? '',
    logo_url: null,
    data: { ...(body.data ?? {}) },
    created_at: now,
    updated_at: now,
  };
  ensure().push(org);
  return org;
}

export function updateOrganizationRecord(id: string, body: UpdateOrganizationRequest): OrganizationSchema | null {
  const list = ensure();
  const idx = list.findIndex((o) => o.id === id);
  if (idx === -1) return null;
  const prev = list[idx];
  const next: OrganizationSchema = {
    ...prev,
    ...(body.name !== undefined ? { name: body.name } : {}),
    ...(body.organization_type !== undefined ? { organization_type: body.organization_type } : {}),
    ...(body.google_maps_url !== undefined ? { google_maps_url: body.google_maps_url } : {}),
    data: { ...prev.data, ...(body.data ?? {}) },
    updated_at: nowNaive(),
  };
  list[idx] = next;
  return next;
}

/** 'protected' for the two demo orgs (they cannot be deleted), else whether it existed. */
export function deleteOrganizationRecord(id: string): 'protected' | 'deleted' | 'missing' {
  if (isDemoOrgId(id)) return 'protected';
  const list = ensure();
  const idx = list.findIndex((o) => o.id === id);
  if (idx === -1) return 'missing';
  list.splice(idx, 1);
  return 'deleted';
}
